import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import LinkIcon from '~/assets/styleButton/link.svg';
import { useEditorStore } from '~/shell/Store';
import { LinkPopup } from '~/ui/link-popup/LinkPopup';

interface EditableLinkButtonProps {
  id: string;
  url: string;
  children?: React.ReactNode;
}

const POPUP_WIDTH = 270;

const Wrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
`;

const iconGlow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 0 rgba(151, 56, 255, 0)); }
  50% { filter: drop-shadow(0 0 6px rgba(151, 56, 255, 0.6)); }
`;

const LinkIconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;

  svg path {
    fill: #9738ff;
  }
`;

const ButtonRow = styled.div`
  display: inline-flex;
  height: 46px;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 24px;
  border-radius: 8px;
  background-color: #ece0ff;
  cursor: pointer;

  &:hover ${LinkIconButton} {
    animation: ${iconGlow} 1.5s ease-in-out infinite;
  }
`;

const TitleWrapper = styled.div`
  color: #9738ff;
  font-weight: bold;

  div,
  p,
  span {
    color: #9738ff !important;
  }
`;

const PopupOverlay = styled.div`
  position: absolute;
  z-index: 2000;
`;

const PopupArrow = styled.div<{ $left: number }>`
  position: absolute;
  top: -6px;
  left: ${p => p.$left}px;
  width: 12px;
  height: 12px;
  background: #fff;
  transform: rotate(45deg);
  box-shadow: -1px -1px 2px rgba(0, 0, 0, 0.08);
`;

const PopupContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 9px;
  padding: 10px 14px;
  max-width: ${POPUP_WIDTH}px;
  overflow: visible;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

export const EditableLinkButton = ({
  id,
  url,
  children
}: EditableLinkButtonProps) => {
  const { updateSequence, updateSequenceInStore } = useEditorStore();
  const [showPopup, setShowPopup] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(url);
  useEffect(() => setCurrentUrl(url), [url]);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [popupPosition, setPopupPosition] = useState({
    top: 0,
    left: 0,
    arrowLeft: 24
  });

  const updatePopupPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const popupWidth = POPUP_WIDTH;
    const viewportWidth = window.innerWidth;
    const margin = 8;

    let popupLeft = rect.left;

    if (popupLeft + popupWidth > viewportWidth - margin) {
      popupLeft = rect.right - popupWidth;
    }

    if (popupLeft < margin) {
      popupLeft = margin;
    }

    const iconBtn = buttonRef.current.querySelector(
      '[data-testid="link-icon-btn"]'
    );
    const iconRect = iconBtn?.getBoundingClientRect();
    const iconCenterAbs = iconRect
      ? iconRect.left - 10 + iconRect.width / 2
      : rect.left + 24;
    const arrowLeft = iconCenterAbs - popupLeft - 6;

    setPopupPosition({
      top: rect.bottom + 8 + window.scrollY,
      left: popupLeft + window.scrollX,
      arrowLeft: Math.max(12, Math.min(arrowLeft, popupWidth - 24))
    });
  }, []);

  useEffect(() => {
    if (!showPopup) return;
    updatePopupPosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('[data-testid="url-popup"]') ||
        target.closest('[data-testid="link-icon-btn"]')
      ) {
        return;
      }
      setShowPopup(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePopupPosition, true);
    window.addEventListener('resize', updatePopupPosition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePopupPosition, true);
      window.removeEventListener('resize', updatePopupPosition);
    };
  }, [showPopup, updatePopupPosition]);

  const handleLinkIconClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPopup(prev => !prev);
  }, []);

  // Backend stores URL in 'subtitle' field; GraphQL maps it back to 'url' on the Button type
  const handleUrlChange = useCallback(
    (newUrl: string) => {
      updateSequence(id, 'subtitle', newUrl);
      updateSequenceInStore(id, 'url', newUrl);
      setCurrentUrl(newUrl);
      setShowPopup(false);
    },
    [id, updateSequence, updateSequenceInStore]
  );

  return (
    <Wrapper>
      <ButtonRow ref={buttonRef}>
        <LinkIconButton
          data-testid="link-icon-btn"
          onClick={handleLinkIconClick}
          type="button"
        >
          <LinkIcon />
        </LinkIconButton>
        <TitleWrapper>{children}</TitleWrapper>
      </ButtonRow>
      {showPopup &&
        createPortal(
          <PopupOverlay
            data-testid="url-popup"
            style={{
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`
            }}
          >
            <PopupContent>
              <PopupArrow $left={popupPosition.arrowLeft} />
              <LinkPopup
                url={currentUrl}
                onUrlChange={handleUrlChange}
                hideDeleteButton
                onCancel={() => setShowPopup(false)}
              />
            </PopupContent>
          </PopupOverlay>,
          document.body
        )}
    </Wrapper>
  );
};
